<template>
  <div class="list-group list-group-flush">

    <!--Total price excluding VAT for Regular and ICHOOSR -->
    <div class="list-group-item" v-if="useBundledPricing" style="border-top: 0px">
      <div class="d-flex flex-wrap">
        <p class="mb-0 mr-3" v-dynamic-content:pv_price_bundle_price></p>
        <span class="ml-auto"> 
          {{ quote.detailOverview.contractPriceExclVATBeforeDiscountWithoutAdders | formatCurrency }}
        </span>
      </div>
    </div>

    <div class="list-group-item" v-else style="border-top: 0px">
      <div class="d-flex flex-wrap">
        <p class="mb-0 mr-3" v-dynamic-content:pv_system_price_excl_vat></p>
        <span class="ml-auto"> 
          {{ quote.detailOverview.contractPriceExclVATBeforeDiscount | formatCurrency }}
        </span>
      </div>
    </div>

    <div class="list-group-item" v-if="useBundledPricing && quote.adders.length">
      <div class="d-flex flex-wrap">
        <p class="h5 mr-3" v-dynamic-content:pv_bundled_price_adders_title></p>
      </div>
      <div class="mt-2">
        <ul class="checks list-unstyled">
          <li v-for="(adder, index) in quote.adders" :key="index">{{ pickProductName(adder) }}</li>
        </ul>

        <price-item
          :dc="{ key: 'pv_bundled_price_adders_price' }"
          :price="quote.detailOverview.addersOnlyExclVATBeforeDiscount"
        />
      </div>
    </div>


    <!-- Taxes -->
    <div class="list-group-item">
      <div class="d-flex flex-wrap">
        <p class="mb-0 mr-auto" v-dynamic-content:pv_system_vat_title></p>
        <span class="ml-auto">
          {{ quote.detailOverview.valueAddedTaxBeforeDiscount | formatCurrency }}
        </span>
      </div>
      <div class="d-flex flex-wrap" v-if="quote.detailOverview.nonTaxableSubtotalBeforeDiscount > 0">
        <p class="ml-md-8 mb-0 mr-auto" v-dynamic-content:pv_system_vat_percentage_of_subtotal="[ 
          $options.filters.formatPercentage(0),
          $options.filters.formatCurrency(quote.detailOverview.nonTaxableSubtotalBeforeDiscount)]"></p>
        <span class="ml-auto">
          {{ 0 | formatCurrency }}
        </span>
      </div>
      <div class="d-flex flex-wrap" v-if="quote.detailOverview.taxableSubtotalExclVATBeforeDiscount > 0">
        <p class="ml-md-8 mb-0 mr-auto" v-dynamic-content:pv_system_vat_percentage_of_subtotal="[
          $options.filters.formatPercentage(valueAddedTaxTariffPercentage), 
          $options.filters.formatCurrency(quote.detailOverview.taxableSubtotalExclVATBeforeDiscount)]">
        </p>
        <span class="ml-auto">
          {{ quote.detailOverview.valueAddedTaxBeforeDiscount | formatCurrency }}
        </span>
      </div>
    </div>


    <!-- discount -->
    <div class="list-group-item" v-if="quote.detailOverview.discount !== 0">
      <div class="d-flex">
        <p class="mr-auto mb-0" v-dynamic-content:pv_system_price_before_discount></p>
        <p class="mb-0"> {{ quote.detailOverview.contractPriceBeforeDiscount | formatCurrency }}</p>
      </div>
      <div class="d-flex">
        <p class="ml-md-8 mb-0 mr-auto" v-dynamic-content:pv_price_discount></p>
        <p class="mb-0">- {{ quote.detailOverview.discount | formatCurrency }}</p>
      </div>
    </div>

    <!-- products and services -->
    <div v-if="showProductsServices" class="list-group-item">
      <div>
        <div class="d-flex flex-wrap" v-if="!iquote.orderPlaced">
          <p class="h5 mr-3" v-dynamic-content:addons_title></p>
        </div>
        <ul class="pl-0">
          <li v-for="(addon, index) in getProductsServices" :key="index" class="d-flex mt-3">
            <products-services-checkbox
              v-if="displayCheckBox"
              :service="addon"
            ></products-services-checkbox>
            <span v-else class="mr-auto">{{ addon.text.title }} {{ content.incl_21_vat }} </span>
            <div class="mr-0" v-if="hasChosenAddons">
              <p class="mb-0" v-show="showProductsServicesPrice(addon.productId)">
                {{ addon.prices.priceInclVAT | formatCurrency }}
              </p>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <!-- total + downpayment -->
    <div class="list-group-item">
      <div>
        <div class="d-flex">
          <p class="h5 mb-0 mr-auto" v-dynamic-content:pv_price_total></p>
          <p class="h5 mb-0">{{ getAmounts.contractPrice | formatCurrency }}</p>
        </div>

        <div class="d-flex">
          <p
            class="mr-auto mb-3 ml-4 mt-3"
            v-if="downPaymentAlreadyPaid"
            v-dynamic-content:summary_price_deposit_already_paid
          ></p>
          <p class="mr-auto mb-3 ml-4 mt-3" v-else v-dynamic-content:summary_price_deposit></p>

          <p class="mb-3 mt-3">- {{ quote.detailOverview.downPaymentAmount | formatCurrency }}</p>
        </div>

        <div class="d-flex mb-3">
          <p class="mb-0 mr-auto ml-4" v-dynamic-content:summary_price_remainder></p>
          <p class="mb-0">{{ getAmounts.remainingPrice | formatCurrency }}</p>
        </div>
      </div>
    </div>

    <!--  downpayment now for payment -->
    <div
      v-if="!downPaymentAlreadyPaid && (!done || content.summary_price_summary_tax)"
      class="list-group-item"
    >
      <div class="mb-3">
        <div class="d-flex h5 mb-0 text-primary" v-if="!done">
          <p class="mb-0 mr-auto" v-dynamic-content:summary_price_tobepaidnow></p>
          <p class="mb-0">{{ quote.detailOverview.downPaymentAmount | formatCurrency }}</p>
        </div>

        <p
          v-if="content.summary_price_summary_tax"
          class="font-size-sm text-muted mb-0"
          v-dynamic-content:summary_price_summary_tax="[
            $options.filters.formatPercentage(valueAddedTaxTariffPercentage),
          ]"
        ></p>
      </div>
    </div>
  </div>
</template>
<script>
import ProductsServicesContent from '@/components/order/ProductsServicesContent'
import ProductsServicesCheckbox from '@/components/order/ProductsServicesCheckbox'
import PriceItem from '@/components/quotes/DetailListPriceBlockItem'
import { mapGetters } from 'vuex'

export default {
  components: {
    ProductsServicesCheckbox,
    PriceItem,
  },
  name: 'OrderSummaryPricing',
  props: ['quote', 'done', 'productsServices', 'showProductsServices'],
  mixins: ['pickProductName'],
  computed: {
    ...mapGetters(['isIChoosr', 'isOpenRedesign']),
    getProductsServices() {
      const all = ProductsServicesContent(this.$store.getters.content, this.pickProductName, this.quote.addons)
      if (this.displayCheckBox) return all
      else
        return all.filter((addon) =>
          this.productsServices.map((p) => p.productId).includes(addon.productId)
        )
    },
    getProductsServicesTotal() {
      return this.productsServices
        ? this.productsServices.reduce((total, { priceInclVAT }) => total + Number(priceInclVAT), 0)
        : 0
    },
    valueAddedTaxTariffPercentage() {
      return this.quote.detailOverview.valueAddedTaxTariffPercentage
    },
    getAmounts() {
      const { contractPrice, downPaymentAmount, remainingPrice } =
        this.quote.detailOverview
      const productsServicesTotal = this.getProductsServicesTotal
      const priceAfterDownPayment = remainingPrice || contractPrice - downPaymentAmount
      return {
        contractPrice: contractPrice + productsServicesTotal,
        remainingPrice: priceAfterDownPayment + productsServicesTotal,
      }
    },
    productsServicesIDs() {
      return this.productsServices ? this.productsServices.map((service) => service.productId) : []
    },
    displayCheckBox() {
      const { iquote } = this.$store.getters
      if (iquote.redesignState === 'redesign_not_approved') return true
      else if (iquote.paymentStatus === 'PAID') return false
      else return !iquote.orderPlaced
    },
    hasChosenAddons() {
      return (
        (this.$store.getters.iquote.chosenAddons &&
          this.$store.getters.iquote.chosenAddons.length) ||
        this.productsServices.length
      )
    },
    downPaymentAlreadyPaid() {
      return this.isIChoosr || this.isOpenRedesign
    },
    useBundledPricing() {
      return this.isIChoosr
    },
  },
  methods: {
    showProductsServicesPrice(id) {
      return this.productsServicesIDs.includes(id) || this.$store.getters.iquote.orderPlaced
    },
  },
}
</script>
